import { Stack, TextField } from '@mui/material';
import useTranslation from 'hooks/useTranslation/useTranslation';
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import DrawerTemplate from 'v2/components/Drawer/DrawerTemplate';
import useDrawerContext from 'v2/components/Drawer/useDrawerContext';
import useSnackbarContext from 'v2/components/Snackbar/useSnackbarContext';

interface Props {
  currentName: string;
  onSave: (name: string) => Promise<{ success: boolean; errorMessage?: string }>;
}

interface FormValues {
  name: string;
}

const FORM_ID = 'edit-device-group-form';

const EditDeviceGroupDrawer: React.FC<Props> = ({ currentName, onSave }) => {
  const { translation } = useTranslation();
  const { closeDrawer } = useDrawerContext();
  const { showSnackbar } = useSnackbarContext();
  const [error, setError] = useState<string>();

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting }
  } = useForm<FormValues>({
    mode: 'onTouched',
    defaultValues: { name: currentName }
  });

  const onSubmit = async (data: FormValues) => {
    setError(undefined);
    const result = await onSave(data.name);
    if (result.success) {
      showSnackbar(translation.device_group_name_updated, { severity: 'success' });
      closeDrawer();
    } else {
      setError(result.errorMessage ?? translation.device_group_error_updating_name);
    }
  };

  return (
    <DrawerTemplate
      loading={isSubmitting}
      title={translation.edit_group_name}
      primaryAction={{
        id: 'save-button',
        label: translation.save,
        onClick: handleSubmit(onSubmit),
        loading: isSubmitting
      }}
      secondaryAction={{ id: 'cancel-button', label: translation.cancel }}
      error={error}
      onDismissError={() => setError(undefined)}
    >
      <Stack component="form" id={FORM_ID} onSubmit={handleSubmit(onSubmit)} noValidate>
        <TextField
          {...register('name', { required: translation.name_cannot_be_empty })}
          id="name-input"
          label={translation.name}
          variant="outlined"
          fullWidth
          autoFocus
          error={!!errors.name}
          helperText={errors.name?.message}
        />
      </Stack>
    </DrawerTemplate>
  );
};

export default EditDeviceGroupDrawer;
